import { MessageRole, ToolName, ToolCallStatus } from "./types";
import { ChatMessage, ToolCallData } from "./stores";
import { firstString, getNestedRecord, unwrapToolOutputRecord } from "./utils/toolOutputParsing";

const formatToolName = (name: string): string =>
  name
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());

const sentenceCase = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const isSmartTableToolName = (name: string): boolean =>
  name.includes("smart_table") || name.includes("smart-table");

const getTableToolResourceLabel = (toolName: string): string | null => {
  const name = toolName.toLowerCase();
  if (!isSmartTableToolName(name)) {
    return null;
  }

  const remainder = name
    .replace(/^(create|update|delete|patch|run|get|list|add|configure|import)_/, "")
    .replace(/^smart_table_?/, "")
    .replace(/_/g, " ")
    .trim();

  if (!remainder) {
    return "Table";
  }

  return `Table ${remainder}`;
};

const getCreateEntityLabel = (toolName: string, fallbackResourceName: string): string => {
  const name = toolName.toLowerCase();

  if (name.includes("input_variable_set")) {
    return "Input variable set";
  }
  if (name.includes("ab_test")) {
    return "A/B test";
  }
  if (name.includes("skill_collection")) {
    return "Skill collection";
  }
  if (name.includes("prompt")) {
    return "Prompt";
  }
  if (name.includes("dataset")) {
    return "Dataset";
  }
  if (name.includes("evaluation") || name.includes("eval")) {
    return "Evaluation";
  }
  if (isSmartTableToolName(name)) {
    return "Table";
  }
  if (name.includes("workflow")) {
    return "Workflow";
  }
  if (name.includes("folder")) {
    return "Folder";
  }

  return sentenceCase(fallbackResourceName);
};

const getCreatedEntityName = (toolCall: ToolCallData): string | undefined => {
  const output = unwrapToolOutputRecord(toolCall.output);
  const input = toolCall.input ?? {};

  const folder = getNestedRecord(output, "folder");
  const promptTemplate = getNestedRecord(output, "prompt_template");
  const prompt = getNestedRecord(output, "prompt");
  const datasetGroup = getNestedRecord(output, "dataset_group");
  const dataset = getNestedRecord(output, "dataset");
  const evaluation = getNestedRecord(output, "evaluation");
  const report = getNestedRecord(output, "report");
  const smartTable = getNestedRecord(output, "smart_table");
  const sheet = getNestedRecord(output, "sheet");
  const column = getNestedRecord(output, "column");
  const blueprint = getNestedRecord(output, "blueprint");
  const workflow = getNestedRecord(output, "workflow");
  const skillCollection = getNestedRecord(output, "skill_collection");
  const inputVariableSet = getNestedRecord(output, "input_variable_set");

  return firstString(
    folder?.name,
    promptTemplate?.prompt_name,
    promptTemplate?.name,
    prompt?.prompt_name,
    prompt?.name,
    datasetGroup?.name,
    dataset?.name,
    evaluation?.name,
    report?.name,
    smartTable?.title,
    sheet?.title,
    column?.title,
    output?.report_name,
    blueprint?.name,
    workflow?.name,
    workflow?.workflow_name,
    skillCollection?.name,
    inputVariableSet?.name,
    output?.prompt_name,
    output?.name,
    input.prompt_name,
    input.name,
  );
};

const getCreateToolDisplayName = (
  toolCall: ToolCallData,
  entityLabel: string,
  createdName: string | undefined,
  isFailed: boolean,
): string => {
  if (isFailed) {
    return sentenceCase(`${entityLabel} creation failed`);
  }
  if (toolCall.status !== ToolCallStatus.COMPLETED) {
    return sentenceCase(`Creating ${entityLabel.toLowerCase()}`);
  }
  if (createdName) {
    return `${entityLabel}: ${createdName}`;
  }
  return `${entityLabel} created`;
};

const getUpdateToolDisplayName = (
  toolCall: ToolCallData,
  resourceName: string,
  isFailed: boolean,
): string => {
  if (isFailed) {
    return sentenceCase(`${resourceName} update failed`);
  }
  if (toolCall.status === ToolCallStatus.COMPLETED) {
    return sentenceCase(`${resourceName} updated`);
  }
  return sentenceCase(`Updating ${resourceName.toLowerCase()}`);
};

const getRunToolDisplayName = (
  toolCall: ToolCallData,
  resourceName: string,
  isFailed: boolean,
): string => {
  if (isFailed) {
    return sentenceCase(`${resourceName} failed`);
  }
  if (toolCall.status === ToolCallStatus.COMPLETED) {
    return sentenceCase(`${resourceName} completed`);
  }
  return sentenceCase(`Running ${resourceName.toLowerCase()}`);
};

export interface ToolDisplayInfo {
  displayName: string;
  type:
    | "planning"
    | "running"
    | "fetching"
    | "finalizing"
    | "updating"
    | "prompt"
    | "model"
    | "search"
    | "creating"
    | "sleeping"
    | "workflow"
    | "dataset"
    | "evaluation"
    | "scorecard"
    | "smart_table"
    | "folder"
    | "skill"
    | "ab_test"
    | "input_variable_set"
    | "tool"
    | "analytics"
    | "trace"
    | "request_log"
    | "env_var"
    | "unknown";
  shouldHideWhenComplete?: boolean;
}

const inferToolType = (name: string): ToolDisplayInfo["type"] => {
  if (name.includes("prompt")) {
    return "prompt";
  }
  if (name.includes("model")) {
    return "model";
  }
  if (name.includes("dataset")) {
    return "dataset";
  }
  if (name.includes("scorecard") || name.includes("smart_table_score")) {
    return "scorecard";
  }
  if (name.includes("evaluation") || name.includes("eval")) {
    return "evaluation";
  }
  if (name.includes("smart_table") || name.includes("smart-table")) {
    return "smart_table";
  }
  if (name.includes("workflow")) {
    return "workflow";
  }
  if (name.includes("folder")) {
    return "folder";
  }
  if (name.includes("ab_test")) {
    return "ab_test";
  }
  if (name.includes("input_variable_set")) {
    return "input_variable_set";
  }
  if (name.includes("tool_registry") || name.includes("tool_env_var")) {
    return "tool";
  }
  if (name.includes("env_var")) {
    return "env_var";
  }
  if (name.includes("skill")) {
    return "skill";
  }
  if (name.includes("analytics") || name.includes("analytics_graph")) {
    return "analytics";
  }
  if (name.includes("trace")) {
    return "trace";
  }
  if (name.includes("request_log") || name.includes("request_search")) {
    return "request_log";
  }
  if (name.includes("search")) {
    return "search";
  }
  return "unknown";
};

export const getToolDisplayInfo = (toolCall: ToolCallData): ToolDisplayInfo => {
  const name = toolCall.name.toLowerCase();
  const input = toolCall.input || {};
  const isFailed =
    toolCall.status === ToolCallStatus.ERROR || toolCall.status === ToolCallStatus.FAILED;

  if (name === ToolName.SLEEP) {
    return {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED ? "Wait completed" : "Waiting briefly",
      type: "sleeping",
      shouldHideWhenComplete: true,
    };
  }

  if (input.description) {
    return {
      displayName: sentenceCase(input.description as string),
      type: "unknown",
    };
  }

  const directMappings: Partial<Record<ToolName, ToolDisplayInfo>> = {
    [ToolName.RETURN_RESULTS]: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED ? "Results finalized" : "Finalizing results",
      type: "finalizing",
      shouldHideWhenComplete: true,
    },
  };

  const directMapping = directMappings[name as ToolName];
  if (directMapping) {
    return directMapping;
  }

  const actionMappings: Record<string, ToolDisplayInfo> = {
    list_model_configs: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Available models checked"
          : "Checking available models",
      type: "model",
    },
    get_model_config: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Model details loaded"
          : "Reading model details",
      type: "model",
      shouldHideWhenComplete: true,
    },
    select_model_config: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Best model selected"
          : "Looking for the best model",
      type: "model",
    },
    search_entities: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Workspace search completed"
          : "Searching workspace",
      type: "search",
    },
    search_tool_history: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED ? "History loaded" : "Searching tool history",
      type: "search",
    },
    structured_request_search: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Request logs searched"
          : "Searching request logs",
      type: "request_log",
    },
    structured_trace_search: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED ? "Traces searched" : "Searching traces",
      type: "trace",
    },
    get_trace_search_suggestions: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Trace filters loaded"
          : "Loading trace filters",
      type: "trace",
    },
    get_trace: {
      displayName: toolCall.status === ToolCallStatus.COMPLETED ? "Trace loaded" : "Loading trace",
      type: "trace",
    },
    get_request_logs: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Request logs loaded"
          : "Loading request logs",
      type: "request_log",
    },
    get_request_analytics: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Request analytics loaded"
          : "Loading request analytics",
      type: "analytics",
    },
    [ToolName.CREATE_ANALYTICS_GRAPH]: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Analytics graph created"
          : "Creating analytics graph",
      type: "analytics",
    },
    get_entity_details: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED ? "Details loaded" : "Loading details",
      type: "fetching",
    },
    delete_smart_table: {
      displayName: isFailed ? "Table deletion failed" : "Table deleted",
      type: "smart_table",
    },
    list_smart_table_sheets: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED
          ? "Table sheets listed"
          : "Listing table sheets",
      type: "smart_table",
    },
    get_smart_table_grid: {
      displayName:
        toolCall.status === ToolCallStatus.COMPLETED ? "Table grid loaded" : "Loading table grid",
      type: "smart_table",
    },
    add_smart_table_rows: {
      displayName: isFailed ? "Table row addition failed" : "Table rows added",
      type: "smart_table",
    },
    import_smart_table_request_history: {
      displayName: isFailed
        ? "Table request history import failed"
        : "Table request history import started",
      type: "request_log",
    },
    import_smart_table_trace_history: {
      displayName: isFailed
        ? "Table trace history import failed"
        : "Table trace history import started",
      type: "trace",
    },
    delete_smart_table_rows: {
      displayName: isFailed ? "Table row deletion failed" : "Table rows deleted",
      type: "smart_table",
    },
    configure_smart_table_score: {
      displayName: isFailed ? "Table score configuration failed" : "Table score configured",
      type: "scorecard",
    },
    configure_smart_table_scorecard: {
      displayName: isFailed ? "Scorecard configuration failed" : "Scorecard configured",
      type: "scorecard",
    },
    get_smart_table_scorecard: {
      displayName: isFailed ? "Scorecard load failed" : "Scorecard loaded",
      type: "scorecard",
    },
    recalculate_smart_table_scorecard: {
      displayName: isFailed ? "Scorecard recalculation failed" : "Scorecard recalculation started",
      type: "scorecard",
    },
  };

  const actionMapping = actionMappings[name];
  if (actionMapping) {
    return actionMapping;
  }

  if (name.startsWith("create_")) {
    const tableResourceLabel = getTableToolResourceLabel(toolCall.name);
    const resourceName = formatToolName(toolCall.name).replace(/^Create /, "");
    const entityLabel = tableResourceLabel ?? getCreateEntityLabel(toolCall.name, resourceName);
    const createdName = getCreatedEntityName(toolCall);
    const inferred = inferToolType(name);
    return {
      displayName: getCreateToolDisplayName(toolCall, entityLabel, createdName, isFailed),
      type: inferred === "unknown" ? "creating" : inferred,
    };
  }

  if (name.startsWith("update_") || name.startsWith("patch_") || name.startsWith("manage_")) {
    const tableResourceLabel = getTableToolResourceLabel(toolCall.name);
    const resourceName = name.includes("ab_test")
      ? "A/B test"
      : (tableResourceLabel ??
        formatToolName(toolCall.name).replace(/^(Update|Patch|Manage) /, ""));
    const inferred = inferToolType(name);
    return {
      displayName: getUpdateToolDisplayName(toolCall, resourceName, isFailed),
      type: inferred === "unknown" ? "updating" : inferred,
    };
  }

  if (name.startsWith("run_")) {
    const tableResourceLabel = getTableToolResourceLabel(toolCall.name);
    const resourceName = tableResourceLabel ?? formatToolName(toolCall.name).replace(/^Run /, "");
    const inferred = inferToolType(name);
    return {
      displayName: getRunToolDisplayName(toolCall, resourceName, isFailed),
      type: inferred === "unknown" ? "running" : inferred,
    };
  }

  if (name === ToolName.COMPUTER_AGENT) {
    return {
      displayName: formatToolName(toolCall.name),
      type: "unknown",
    };
  }

  const tableResourceLabel = getTableToolResourceLabel(toolCall.name);
  const formattedName = tableResourceLabel ?? formatToolName(toolCall.name);

  return {
    displayName: formattedName,
    type: isSmartTableToolName(name) ? "smart_table" : "unknown",
  };
};

/**
 * Latest in-flight (running/pending) tool from the end of the thread, for status lines
 * (e.g. “Creating folder…” instead of “Thinking…” while a tool runs).
 */
export const getActiveToolProgressLabel = (messages: readonly ChatMessage[]): string | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== MessageRole.TOOL_CALL || !msg.toolCall) {
      continue;
    }
    const { name, status } = msg.toolCall;
    if (name?.toLowerCase() === ToolName.COMPUTER_AGENT) {
      continue;
    }
    if (status === ToolCallStatus.RUNNING || status === ToolCallStatus.PENDING) {
      return getToolDisplayInfo(msg.toolCall).displayName;
    }
  }
  return null;
};

export const getLatestToolProgressLabel = (messages: readonly ChatMessage[]): string | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== MessageRole.TOOL_CALL || !msg.toolCall) {
      continue;
    }
    const toolName = msg.toolCall.name?.toLowerCase();
    if (toolName === ToolName.COMPUTER_AGENT) {
      continue;
    }
    return getToolDisplayInfo(msg.toolCall).displayName;
  }
  return null;
};
